import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionMarkCircleIcon, XMarkIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface FloatingFAQButtonProps {
  className?: string;
}

const FloatingFAQButton = ({ className = '' }: FloatingFAQButtonProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [showReappearButton, setShowReappearButton] = useState(false);

  const handleFAQClick = () => {
    // Navigate to FAQ page
    navigate('/faq');
  };

  const handleHideButton = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsHidden(true);
    setShowReappearButton(true);
  };

  const handleShowButton = () => {
    setIsHidden(false);
    setShowReappearButton(false);
  };

  // Auto-hide reappear button after 5 seconds
  useEffect(() => {
    if (showReappearButton) {
      const timer = setTimeout(() => {
        setShowReappearButton(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showReappearButton]);

  return (
    <>
      {/* Main FAQ Button */}
      <AnimatePresence>
        {!isHidden && (
          <motion.div
            className={`fixed bottom-6 right-6 z-50 ${className}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3, delay: isHidden ? 0 : 1 }}
          >
      {/* Tooltip */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, x: 10, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap shadow-lg"
          >
            {t('faq.floatingButton.tooltip')}
            <div className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-l-4 border-l-gray-900 dark:border-l-gray-100 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
          </motion.div>
        )}
      </AnimatePresence>

            {/* Main Button */}
            <motion.button
              onClick={handleFAQClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="group relative bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:focus:ring-primary-800"
              aria-label={t('faq.floatingButton.ariaLabel')}
            >
              <QuestionMarkCircleIcon className="h-6 w-6" />
              
              {/* X Button */}
              <button
                onClick={handleHideButton}
                className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                aria-label={t('faq.floatingButton.hideButton')}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
              
              {/* Pulse animation */}
              <motion.div
                className="absolute inset-0 rounded-full bg-primary-500 dark:bg-primary-600"
                animate={{ scale: [1, 1.2, 1], opacity: [0.7, 0, 0.7] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.button>

            {/* Ripple effect on click */}
            <motion.div
              className="absolute inset-0 rounded-full bg-white opacity-30"
              initial={{ scale: 0, opacity: 0 }}
              whileTap={{ scale: 1.5, opacity: 0 }}
              transition={{ duration: 0.6 }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reappear Button */}
      <AnimatePresence>
        {showReappearButton && (
          <motion.div
            className="fixed right-6 top-1/2 -translate-y-1/2 z-50"
            initial={{ opacity: 0, x: 20, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <motion.button
              onClick={handleShowButton}
              whileHover={{ scale: 1.1, x: -2 }}
              whileTap={{ scale: 0.95 }}
              className="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500 text-white p-2 rounded-l-full shadow-lg hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:focus:ring-primary-800 border-r-0"
              aria-label={t('faq.floatingButton.showButton')}
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingFAQButton;
